import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useSearchParams, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { fetchItemById, clearCurrentItem, clearError } from '../../store/slices/itemsSlice';
import { addToCart, fetchCart } from '../../store/slices/cartSlice';
import { startNegotiation } from '../../store/slices/negotiationsSlice';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SEO from 'components/seo/SEO';
import { buildProductSchema, buildBreadcrumbSchema } from '../../utils/seoStructuredData';
import axios from 'axios';
import styled from 'styled-components';
import { itemsAPI } from '../../api/itemsAPI';
import { useToast } from '../../components/common/Toast';
import { logger } from '../../utils/logger';
import { getItemPublicPath, getMarketplaceItemDetailPath, getEditListingPath, isNumericListingRef, isMarketplaceItemCodeRef } from '../../utils/itemLinks';
import { negotiationsAPI } from '../../api/negotiationsAPI';
import { resolveMarketplaceItemBySlug } from '../../utils/resolveMarketplaceItemBySlug';
import { parseWeddingListingDescription } from '../../utils/parseWeddingListingDescription';
import { formatItemPriceEgp, formatListingPriceLabel } from '../../utils/formatPrice';
import { trackItemViewed, trackItemAddedToCart } from '../../utils/analytics';
import { flagAPI, scoresAPI } from '../../api/itemEngagementAPI';
import { trackItemFlagged } from '../../utils/analytics';
import SellerIdentityRow from '../../components/items/SellerIdentityRow';
import SellerStatsLabels from '../../components/items/SellerStatsLabels';
import ItemImageGallery from '../../components/items/ItemImageGallery';
import OpenInFleaBanner from '../../components/items/OpenInFleaBanner';
import { collectListingImageUrls } from '../../utils/listingImages';
import GuestAuthPrompt from '../../components/auth/GuestAuthPrompt';
import { navigateRequireAuth } from '../../utils/authRedirect';
import {
  PlatformBenefitChips,
  ListingMetaFields,
  AnonymousSellerNotice,
  ReceiptAvailableBadge,
  ElectronicsListingNotice,
  FreeListingNotice,
  isComputersElectronicsListing,
  isListingAnonymous,
  isLuxuryListing,
  resolveListingBrand,
} from '../../components/items/ListingDetailShared';
import { addRecentlyViewed } from '../../utils/recentlyViewed';
import getAuraApiBaseUrl from '../../api/auraApiBase';
import { formatAuraScoreDisplay } from '../../utils/formatAuraScore';
import { useTranslation } from 'react-i18next';
import { supportsWeddingFittingAppointment } from '../../constants/weddingCategories';

const WEDDING_ESSENTIAL_CATEGORIES = new Set([
  'Bridal Gowns and Essentials',
  'Bridesmaids Dresses',
  'Bridal Veil',
  'Bridal Accessories',
  'Bridal Shoes',
  'Groom Suits & Essentials',
  'Grooms Accessories',
  'Event Planning Services',
  'Event Decorations',
]);

const DetailPageShell = styled.div`
  min-height: 100vh;
  background: #f6f1e8;
`;

const DetailContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const ItemDetail = styled.div`
  background-color: transparent;
  border-radius: 0.5rem;
  box-shadow: none;
  padding: 2rem;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const ItemTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const HeaderButton = styled.button`
  padding: 0.45rem 0.9rem;
  border: none;
  border-radius: 999px;
  color: white;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }
`;

const HeaderEditButton = styled(HeaderButton)`
  background-color: var(--primary-color);
`;

const ShareButton = styled(HeaderButton)`
  background-color: #1b4332;
`;

const DefectedBanner = styled.div`
  margin: 0 0 1rem;
  padding: 0.85rem 1rem;
  border-radius: 0.6rem;
  background: #fef3c7;
  border: 1px solid rgba(146, 64, 14, 0.2);
  color: #92400e;
`;

const DefectedBannerTitle = styled.div`
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const DefectedBannerBody = styled.div`
  font-size: 0.92rem;
  line-height: 1.4;
`;

const ItemPrice = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: var(--primary-color);
  color: white;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
  }
`;

const AddToCartButton = styled(PrimaryButton)`
  background-color: #22c55e;

  &:hover:not(:disabled) {
    background-color: #16a34a;
  }
`;

const DangerButton = styled(Button)`
  background-color: #fff;
  color: #b91c1c;
  border: 1px solid #fecaca;

  &:hover:not(:disabled) {
    background-color: #fef2f2;
    border-color: #f87171;
  }
`;

const FlagButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 1.1rem;
  padding: 0.4rem 0.6rem;
  border-radius: 0.375rem;
  transition: color 0.2s, background 0.2s;
  &:hover { color: #ef4444; background: #fef2f2; }
  title { display: none; }
`;

const FlagOverlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
`;

const FlagModal = styled.div`
  background: #fff;
  border-radius: 1rem;
  padding: 1.75rem;
  width: 100%;
  max-width: 360px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
`;

const FlagModalTitle = styled.h3`
  font-size: 1.1rem; font-weight: 700; color: #1e293b; margin: 0 0 0.35rem;
`;

const FlagModalSubtitle = styled.p`
  font-size: 0.85rem; color: #64748b; margin: 0 0 1.25rem;
`;

const FlagReasonButton = styled.button`
  display: block; width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  border: 2px solid ${p => p.$selected ? '#ef4444' : '#e5e7eb'};
  border-radius: 0.5rem;
  background: ${p => p.$selected ? '#fef2f2' : '#fff'};
  color: #1e293b; font-size: 0.9rem; font-weight: ${p => p.$selected ? '700' : '500'};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #ef4444; background: #fef2f2; }
`;

const FlagSubmitButton = styled.button`
  width: 100%; margin-top: 0.75rem;
  padding: 0.75rem;
  background: #ef4444; color: #fff;
  border: none; border-radius: 0.5rem;
  font-weight: 700; font-size: 0.95rem;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #dc2626; }
`;

const FlagCancelButton = styled.button`
  width: 100%; margin-top: 0.5rem;
  padding: 0.65rem;
  background: none; color: #64748b;
  border: 1px solid #e5e7eb; border-radius: 0.5rem;
  font-size: 0.9rem; cursor: pointer;
  &:hover { background: #f8fafc; }
`;

// const SecondaryButton = styled(Button)`
//   background-color: transparent;
//   color: var(--text-primary);
//   border: 2px solid var(--border-color);
//   
//   &:hover:not(:disabled) {
//     background-color: var(--bg-secondary);
//   }
// `;

const SellerAuraScore = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 1rem;
`;

const FLAG_REASONS = ['Scam', 'Not as described', 'Damaged', 'Objectionable content', 'Abusive user'];
const FLAG_REASON_LABEL_KEYS = {
  Scam: 'itemDetail.flagScam',
  'Not as described': 'itemDetail.flagNotAsDescribed',
  Damaged: 'itemDetail.flagDamaged',
  'Objectionable content': 'itemDetail.flagObjectionable',
  'Abusive user': 'itemDetail.flagAbusive',
};

const ItemDetailPage = () => {
  const { t } = useTranslation();
  const { itemRef, categorySlug, itemSlug } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentItem, loading } = useSelector((state) => state.items);
  const { user } = useSelector((state) => state.auth);
  const { loading: cartLoading } = useSelector((state) => state.cart);
  const { loading: negotiationsLoading } = useSelector((state) => state.negotiations);
  const { showSuccess, showError } = useToast();
  const [slugNotFound, setSlugNotFound] = useState(false);
  const [slugResolving, setSlugResolving] = useState(false);
  const [ownerAura, setOwnerAura] = useState(null);
  const [auraLoading, setAuraLoading] = useState(false);
  const [authenticityLoading, setAuthenticityLoading] = useState(false);
  const [fittingLoading, setFittingLoading] = useState(false);
  const [authenticityModalOpen, setAuthenticityModalOpen] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);

  const handleShareListing = async () => {
    const url = `${window.location.origin}${getMarketplaceItemDetailPath(currentItem)}`;
    const shareData = {
      title: currentItem.title,
      text: currentItem.title,
      url,
    };

    try {
      if (typeof navigator.share === 'function') {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Listing link copied');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        toast.error('Could not share this listing');
      }
    }
  };

  const handleDeleteListing = async () => {
    if (!currentItem?.id) return;
    const confirmed = window.confirm(
      `Delete "${currentItem.title}"? It will be removed from your catalogue and the storefront.`
    );
    if (!confirmed) return;
    try {
      setDeletingItem(true);
      await itemsAPI.deleteItem(currentItem.id);
      toast.success('Item deleted');
      navigate('/my-catalogue');
    } catch (err) {
      const message =
        err?.response?.data?.error || 'Failed to delete item. Please try again.';
      toast.error(typeof message === 'string' ? message : 'Failed to delete item');
    } finally {
      setDeletingItem(false);
    }
  };

  const handleFlagSubmit = async () => {
    if (!flagReason) return;
    setFlagSubmitting(true);
    try {
      await flagAPI.flagListing(currentItem.id, 'item', flagReason);
      trackItemFlagged(currentItem.id, 'item', flagReason);
      toast.success(t('itemDetail.reportThankYou'));
      setFlagModalOpen(false);
      setFlagReason('');
    } catch (err) {
      const msg = err?.response?.data?.error || t('itemDetail.reportFailed');
      toast.error(msg);
    } finally {
      setFlagSubmitting(false);
    }
  };

  const stateItemId = location?.state?.itemId;
  const queryItemId = searchParams.get('id');
  const directNumericId =
    (itemRef && isNumericListingRef(String(itemRef)) ? itemRef : null) ||
    (queryItemId && isNumericListingRef(String(queryItemId)) ? queryItemId : null);
  const itemCodeRef = itemRef && isMarketplaceItemCodeRef(itemRef) ? String(itemRef).trim() : null;
  const requestedItemId =
    directNumericId ?? stateItemId ?? null;

  useEffect(() => {
    const loadItem = async () => {
      dispatch(clearError());
      dispatch(clearCurrentItem());
      setSlugNotFound(false);

      if (directNumericId) {
        dispatch(fetchItemById(directNumericId));
        return;
      }
      if (itemCodeRef) {
        dispatch(fetchItemById(itemCodeRef));
        return;
      }
      if (stateItemId) {
        dispatch(fetchItemById(stateItemId));
        return;
      }
      if (categorySlug && itemSlug) {
        try {
          setSlugResolving(true);
          const matchedItem = await resolveMarketplaceItemBySlug(itemsAPI, categorySlug, itemSlug);

          if (matchedItem?.id) {
            dispatch(fetchItemById(matchedItem.id));
          } else {
            setSlugNotFound(true);
          }
        } catch (error) {
          setSlugNotFound(true);
        } finally {
          setSlugResolving(false);
        }
      }
    };

    loadItem();
  }, [dispatch, directNumericId, itemCodeRef, categorySlug, itemSlug, stateItemId]);

  useEffect(() => {
    if (currentItem?.id && itemRef && isNumericListingRef(itemRef) && !categorySlug) {
      navigate(getItemPublicPath(currentItem), {
        replace: true,
        state: { itemId: currentItem.id },
      });
    }
  }, [currentItem, itemRef, categorySlug, navigate]);

  // Debug: Log ownership check (always log, even if values are missing)
  useEffect(() => {
    logger.log('🔍 Item Ownership Debug:', {
      hasCurrentItem: !!currentItem,
      hasUser: !!user,
      itemId: currentItem?.id,
      itemTitle: currentItem?.title,
      itemOwnerId: currentItem?.owner_id,
      userId: user?.id,
      userEmail: user?.email,
      isOwner: user?.id === currentItem?.owner_id,
      userObject: user,
      itemObject: currentItem,
      fullState: {
        currentItem,
        user,
        loading
      }
    });
  }, [currentItem, user, loading]);

  // Track item view when item loads
  useEffect(() => {
    if (currentItem?.id) {
      addRecentlyViewed(currentItem);
      trackItemViewed(currentItem.id, {
        item_category: currentItem.category,
        item_price: parseFloat(currentItem.price || 0),
        source: 'list',
      });
    }
  }, [currentItem]);

  // Fetch seller aura from the aura service (DB-backed score).
  useEffect(() => {
    const fetchOwnerAura = async () => {
      if (!currentItem?.owner_id) {
        setOwnerAura(null);
        return;
      }

      try {
        setAuraLoading(true);
        const auraApiUrl = getAuraApiBaseUrl();
        const response = await axios.get(`${auraApiUrl}/api/aura/public/${currentItem.owner_id}/`, {
          withCredentials: true,
        });
        setOwnerAura(response.data);
      } catch (error) {
        logger.error('Error fetching owner aura:', error);
        setOwnerAura(null);
      } finally {
        setAuraLoading(false);
      }
    };

    fetchOwnerAura();
  }, [currentItem]);

  const handleAddToCart = async () => {
    if (!currentItem) return;
    const rawType = String(currentItem.listing_type || '').toLowerCase();
    const listingType = (
      rawType === 'luxury'
        ? 'pre-owned'
        : (currentItem.listing_type
          || (WEDDING_ESSENTIAL_CATEGORIES.has(currentItem.category) ? 'wedding_essentials' : 'pre-owned'))
    );
    
    try {
      await dispatch(addToCart({
        listing_id: currentItem.id,
        listing_type: listingType,
        quantity: 1
      })).unwrap();
      trackItemAddedToCart(currentItem.id, {
        item_price: parseFloat(currentItem.price || 0),
        quantity: 1,
      });
      scoresAPI.recordCartAdd(currentItem.id, 'item').catch(() => {});
      // Fetch updated cart after adding item
      await dispatch(fetchCart());
      
      // Show success message using secure Toast component (XSS protected)
      showSuccess(`✅ ${t('itemDetail.addedToCart')}`, 3000);
      
    } catch (error) {
      logger.error('Error adding to cart:', error);
      
      const errorMessage = error?.message || t('itemDetail.addToCartFailed');
      showError(`❌ ${errorMessage}`, 4000);
    }
  };

  const handleRequestAuthenticityCheck = async () => {
    if (!currentItem || !user) {
      navigateRequireAuth(navigate, location, {
        returnTo: currentItem ? getItemPublicPath(currentItem) : undefined,
      });
      return;
    }

    if (currentItem.owner_id === user.id) {
      toast.error(t('itemDetail.cannotAuthOwn'));
      return;
    }

    setAuthenticityLoading(true);
    try {
      await itemsAPI.requestAuthenticityCheck(currentItem.id);
      setAuthenticityModalOpen(false);
      toast.success(t('itemDetail.authSubmitted'));
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || t('itemDetail.authFailed');
      toast.error(errorMessage);
    } finally {
      setAuthenticityLoading(false);
    }
  };

  const openAuthenticityConfirm = () => {
    if (!currentItem || !user) {
      navigateRequireAuth(navigate, location, {
        returnTo: currentItem ? getItemPublicPath(currentItem) : undefined,
      });
      return;
    }
    if (currentItem.owner_id === user.id) {
      toast.error(t('itemDetail.cannotAuthOwn'));
      return;
    }
    setAuthenticityModalOpen(true);
  };

  const handleReserveFittingAppointment = async () => {
    if (!currentItem || !user) {
      navigateRequireAuth(navigate, location, {
        returnTo: currentItem ? getItemPublicPath(currentItem) : undefined,
      });
      return;
    }

    if (currentItem.owner_id === user.id) {
      toast.error(t('itemDetail.cannotFittingOwn'));
      return;
    }

    setFittingLoading(true);
    try {
      await itemsAPI.requestWeddingFittingAppointment(currentItem.id);
      toast.success(t('itemDetail.fittingSubmitted'));
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || t('itemDetail.fittingFailed');
      toast.error(errorMessage);
    } finally {
      setFittingLoading(false);
    }
  };

  const handleNegotiate = async () => {
    if (!currentItem || !user) {
      navigateRequireAuth(navigate, location, {
        returnTo: currentItem ? getItemPublicPath(currentItem) : undefined,
      });
      return;
    }

    if (currentItem.owner_id === user.id) {
      toast.error(t('itemDetail.cannotNegotiateOwn'));
      return;
    }

    try {
      const result = await dispatch(startNegotiation({
        itemId: currentItem.id,
        sellerId: currentItem.owner_id,
      })).unwrap();
      
      toast.success(t('itemDetail.negotiationStarted'));
      navigate(`/negotiations/${negotiationsAPI.buildNegotiationRef(result.id)}`);
    } catch (error) {
      logger.error('Negotiation error:', error);
      const errorMessage = error?.error || error?.message || (typeof error === 'string' ? error : t('itemDetail.negotiationFailed'));
      toast.error(errorMessage);
    }
  };


  const itemMismatch =
    requestedItemId != null &&
    currentItem?.id != null &&
    Number(currentItem.id) !== Number(requestedItemId);

  if (loading || slugResolving || itemMismatch) {
    return (
      <DetailPageShell>
        <LoadingSpinner text={t('itemDetail.loading')} />
      </DetailPageShell>
    );
  }

  if (slugNotFound || !currentItem) {
    return (
      <DetailPageShell>
        <DetailContainer>
          <div>{t('itemDetail.notFound')}</div>
        </DetailContainer>
      </DetailPageShell>
    );
  }

  const listingDescription = parseWeddingListingDescription(currentItem.description).cleanDescription;
  const isContactOnly = isComputersElectronicsListing(currentItem);
  const showFittingAppointment = supportsWeddingFittingAppointment(currentItem.category);
  const itemTitle = `${currentItem.title} - Used ${currentItem.category} in Egypt | Flea Marketplace`;
  const itemDescription = isContactOnly
    ? `${currentItem.title} - Contact-only listing on Flea (${formatItemPriceEgp(currentItem.price)}, contact seller for inspection & delivery). ${listingDescription?.substring(0, 150) || currentItem.title}...`
    : `Buy ${currentItem.title} - ${currentItem.category} in ${currentItem.condition} condition for ${formatItemPriceEgp(currentItem.price)}. ${listingDescription?.substring(0, 150) || currentItem.title}...`;
  const itemKeywords = currentItem 
    ? `buy ${currentItem.category}, used ${currentItem.category} Egypt, ${currentItem.title}, flea market ${currentItem.category}`
    : 'used items, flea market Egypt';

  const itemPath = currentItem ? getItemPublicPath(currentItem) : '/pre-owned-items';
  const galleryImages = collectListingImageUrls(currentItem);
  const itemJsonLd = currentItem
    ? [
        buildProductSchema(currentItem, itemPath),
        buildBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Browse', url: '/pre-owned-items' },
          { name: currentItem.category, url: `/buy/${String(currentItem.category || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}` },
          { name: currentItem.title, url: itemPath },
        ]),
      ].filter(Boolean)
    : null;

  return (
    <DetailPageShell>
    <DetailContainer>
      <SEO 
        title={itemTitle}
        description={itemDescription}
        keywords={itemKeywords}
        url={itemPath}
        image={currentItem?.primary_image || currentItem?.image_url || '/images/flea-logo.png'}
        type="product"
        jsonLd={itemJsonLd}
      />
      <ItemDetail>
        <ItemImageGallery images={galleryImages} alt={currentItem.title} beige />
        <TitleRow>
          <ItemTitle>{currentItem.title}</ItemTitle>
          <HeaderActions>
            {user && user.id === currentItem.owner_id && (
              <HeaderEditButton
                type="button"
                onClick={() => navigate(getEditListingPath(currentItem))}
              >
                Edit
              </HeaderEditButton>
            )}
            <ShareButton type="button" onClick={handleShareListing}>
              Share
            </ShareButton>
          </HeaderActions>
        </TitleRow>
        <OpenInFleaBanner path={getMarketplaceItemDetailPath(currentItem)} />
        <AnonymousSellerNotice isAnonymous={currentItem.is_anonymous} />
        <ReceiptAvailableBadge item={currentItem} />
        {Boolean(currentItem.is_defected) && (
          <DefectedBanner>
            <DefectedBannerTitle>Item Condition Notice</DefectedBannerTitle>
            <DefectedBannerBody>
              This listing is marked as defected. Review the description and photos carefully before purchasing.
            </DefectedBannerBody>
          </DefectedBanner>
        )}
        {!isListingAnonymous(currentItem.is_anonymous) && (
          <>
            <SellerIdentityRow
              name={currentItem.seller_display_name}
              username={currentItem.seller_display_username}
              profilePicture={currentItem.seller_profile_picture}
            />
            <SellerStatsLabels
              successfulSales={currentItem.seller_successful_sales}
              memberSinceYear={currentItem.seller_member_since_year}
            />
          </>
        )}
        {ownerAura && !auraLoading && !isListingAnonymous(currentItem.is_anonymous) && (
          <SellerAuraScore>
            <span>⭐</span>
            <span>{t('itemDetail.sellerAura', { score: formatAuraScoreDisplay(ownerAura.score) })}</span>
          </SellerAuraScore>
        )}
        <ItemPrice>{formatListingPriceLabel(currentItem)}</ItemPrice>
        <FreeListingNotice item={currentItem} />

        {isContactOnly ? (
          <ElectronicsListingNotice item={currentItem} />
        ) : (
          <PlatformBenefitChips item={currentItem} />
        )}

        <ListingMetaFields
          description={currentItem.description}
          category={currentItem.category}
          subcategory={currentItem.subcategory}
          bookSubcategory={currentItem.book_subcategory}
          brand={resolveListingBrand(currentItem)}
          condition={currentItem.condition}
          color={currentItem.color}
          size={currentItem.size}
          availableSizes={currentItem.available_sizes}
          material={currentItem.material}
          length={currentItem.length}
          bookType={currentItem.book_type}
          bookLanguage={currentItem.book_language}
          bookAudience={currentItem.book_audience}
          petType={currentItem.pet_type}
          isHijabi={currentItem.is_hijabi}
          fragile={currentItem.fragile}
          weightOver15kg={currentItem.weight_over_15kg}
          itemCode={currentItem.item_code}
          itemFlaws={currentItem.item_flaws}
        />

                <ActionButtons>
                  {user && user.id !== currentItem.owner_id && (
                    <>
                      {showFittingAppointment && (
                        <PrimaryButton
                          onClick={handleReserveFittingAppointment}
                          disabled={fittingLoading}
                          style={{
                            backgroundColor: '#be185d',
                            opacity: fittingLoading ? 0.7 : 1,
                            cursor: fittingLoading ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {fittingLoading ? t('itemDetail.submitting') : t('itemDetail.reserveFitting')}
                        </PrimaryButton>
                      )}

                      {!isContactOnly && (
                      <AddToCartButton
                        onClick={handleAddToCart}
                        disabled={!currentItem.in_stock || cartLoading}
                        style={{
                          opacity: cartLoading ? 0.7 : 1,
                          cursor: cartLoading ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {cartLoading ? t('itemDetail.addingToCart') : (currentItem.in_stock ? t('itemDetail.addToCart') : t('itemDetail.outOfStock'))}
                      </AddToCartButton>
                      )}
                      
                      <PrimaryButton 
                        onClick={handleNegotiate}
                        disabled={negotiationsLoading}
                        style={{
                          backgroundColor: '#ff9800',
                          opacity: negotiationsLoading ? 0.7 : 1,
                          cursor: negotiationsLoading ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {negotiationsLoading ? t('itemDetail.starting') : t('itemDetail.negotiate')}
                      </PrimaryButton>
                      
                      {!isContactOnly && isLuxuryListing(currentItem) && (
                        <PrimaryButton 
                          onClick={openAuthenticityConfirm}
                          disabled={authenticityLoading}
                          style={{
                            backgroundColor: '#9c27b0',
                            opacity: authenticityLoading ? 0.7 : 1,
                            cursor: authenticityLoading ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {authenticityLoading ? t('itemDetail.submitting') : t('itemDetail.requestAuthenticity')}
                        </PrimaryButton>
                      )}
                    </>
                  )}
                  {user && user.id === currentItem.owner_id && (
                    <>
                      <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic', margin: 0 }}>
                        {isContactOnly
                          ? t('itemDetail.ownItemFurniture')
                          : t('itemDetail.ownItem')}
                      </p>
                      <PrimaryButton
                        type="button"
                        onClick={() =>
                          navigate(getEditListingPath(currentItem))
                        }
                      >
                        Edit listing
                      </PrimaryButton>
                      <DangerButton
                        type="button"
                        disabled={deletingItem}
                        onClick={handleDeleteListing}
                      >
                        {deletingItem ? 'Deleting…' : 'Delete listing'}
                      </DangerButton>
                    </>
                  )}
                  {!user && (
                    <GuestAuthPrompt
                      message={
                        isContactOnly
                          ? t('itemDetail.loginFurniture')
                          : 'Create an account to continue'
                      }
                      returnTo={getItemPublicPath(currentItem)}
                    />
                  )}
                </ActionButtons>

                {/* Flag button — visible to logged-in users who don't own the item */}
                {user && user.id !== currentItem.owner_id && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <FlagButton
                      type="button"
                      title={t('itemDetail.reportTitle')}
                      onClick={() => { setFlagModalOpen(true); setFlagReason(''); }}
                    >
                      {t('itemDetail.reportListing')}
                    </FlagButton>
                  </div>
                )}
      </ItemDetail>
    </DetailContainer>

    {/* Flag modal */}

    {flagModalOpen && (
      <FlagOverlay onClick={() => setFlagModalOpen(false)}>
        <FlagModal onClick={e => e.stopPropagation()}>
          <FlagModalTitle>{t('itemDetail.reportTitle')}</FlagModalTitle>
          <FlagModalSubtitle>{t('itemDetail.selectReportReason')}</FlagModalSubtitle>
          {FLAG_REASONS.map(r => (
            <FlagReasonButton
              key={r}
              type="button"
              $selected={flagReason === r}
              onClick={() => setFlagReason(r)}
            >
              {t(FLAG_REASON_LABEL_KEYS[r])}
            </FlagReasonButton>
          ))}
          <FlagSubmitButton
            type="button"
            disabled={!flagReason || flagSubmitting}
            onClick={handleFlagSubmit}
          >
            {flagSubmitting ? t('itemDetail.submitting') : t('itemDetail.submitReport')}
          </FlagSubmitButton>
          <FlagCancelButton type="button" onClick={() => setFlagModalOpen(false)}>
            {t('common.cancel')}
          </FlagCancelButton>
        </FlagModal>
      </FlagOverlay>
    )}

    {authenticityModalOpen && (
      <FlagOverlay onClick={() => !authenticityLoading && setAuthenticityModalOpen(false)}>
        <FlagModal onClick={e => e.stopPropagation()}>
          <FlagModalTitle>{t('itemDetail.authenticityTitle')}</FlagModalTitle>
          <FlagModalSubtitle>{t('itemDetail.authenticityBody')}</FlagModalSubtitle>
          <p style={{ margin: '0 0 1.25rem', color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: '0.9rem' }}>
            {t('itemDetail.authenticityNote')}
          </p>
          <FlagSubmitButton
            type="button"
            disabled={authenticityLoading}
            onClick={handleRequestAuthenticityCheck}
            style={{ background: '#9c27b0' }}
          >
            {authenticityLoading ? t('itemDetail.submitting') : t('itemDetail.completeRequest')}
          </FlagSubmitButton>
          <FlagCancelButton
            type="button"
            disabled={authenticityLoading}
            onClick={() => setAuthenticityModalOpen(false)}
          >
            {t('common.cancel')}
          </FlagCancelButton>
        </FlagModal>
      </FlagOverlay>
    )}
    </DetailPageShell>
  );
};

export default ItemDetailPage;
